import { Carousel, Heading } from '@pcln/horizon'
import { ProductSummaryView } from '@pcln/horizon-customer-growth-components'
import { useEffect } from 'react'

import { fireSimilarHotelsClickEvent, fireSimilarHotelsLoadEvent } from '@/components/TripActivity/ga4'
import type { HotelDealMatch } from '@/components/TripActivity/types'

import { tripActivityVariants } from './TripActivity.variant'

type SimilarHotelsProps = {
  similarHotels: HotelDealMatch[]
  isLoading: boolean
  pageName: 'homepage' | 'landing'
  useSmallerHeading?: boolean
}

export default function SimilarHotels({
  similarHotels,
  isLoading,
  pageName,
  useSmallerHeading,
}: Readonly<SimilarHotelsProps>) {
  const { similarHotelsHeading } = tripActivityVariants({ useSmallerHeading })
  useEffect(() => {
    if (!isLoading && similarHotels.length > 0) {
      fireSimilarHotelsLoadEvent({ displayedHotels: similarHotels, pageName })
    }
  }, [isLoading, similarHotels, pageName])

  if (isLoading || similarHotels.length === 0) {
    return null
  }
  return (
    <div className='flex flex-col gap-2 sm:gap-3'>
      <Heading as='h4' className={similarHotelsHeading()}>
        Similar hotels
      </Heading>
      <Carousel
        ariaLabel='Similar hotels'
        dragFree
        numSlides='one-two-three'
        slideWidth='same'
        pagination='numbers'
        type='content'
        slides={similarHotels.map((card, cardIndex) => {
          const { hotelId, linkUrl, numRooms, numNights, reference } = card
          const {
            name,
            thumbnailUrl,
            location,
            starRating,
            totalReviewCount,
            overallGuestRating,
            ratesSummary,
          } = reference
          return {
            id: hotelId,
            childrenNodes: (
              <ProductSummaryView
                key={hotelId}
                url={linkUrl}
                onClick={() => fireSimilarHotelsClickEvent({ hotel: card, index: cardIndex + 1, pageName })}
                productType='hotel'
                imageSrc={thumbnailUrl || ''}
                summaryHeader={name}
                summaryBody={location.neighborhoodName || ''}
                reviewScore={{
                  rating: overallGuestRating ? String(overallGuestRating) : '',
                  reviewCount: totalReviewCount ? String(totalReviewCount) : '',
                  stars: starRating ? String(starRating) : '',
                }}
                roomInfo={
                  ratesSummary?.grandTotalExcludingTax
                    ? {
                        numRooms: numRooms,
                        numNights: numNights,
                        price: String(Math.round(+ratesSummary.grandTotalExcludingTax)),
                        currencySymbol: ratesSummary.minCurrencyCodeSymbol,
                      }
                    : undefined
                }
              />
            ),
          }
        })}
      />
    </div>
  )
}
